// pages/setup/components/SitePreview.tsx
import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    RefreshCw,
    Code,
    MousePointerClick,
    X,
    Monitor,
    Smartphone,
    Tablet
} from 'lucide-react';
import { getPreviewHtml } from '@/services/api';

type DeviceMode = 'desktop' | 'tablet' | 'mobile';

interface DeviceConfig {
    width: number;
    height: number;
    label: string;
}

const DEVICE_CONFIGS: Record<DeviceMode, DeviceConfig> = {
    desktop: {
        width: 1920,
        height: 1080,
        label: 'Desktop'
    },
    tablet: {
        width: 768,
        height: 1024,
        label: 'Tablet'
    },
    mobile: {
        width: 375,
        height: 812,
        label: 'Mobile'
    }
};

interface SitePreviewProps {
    selectedBlock: string;
    domain?: string;
    blockConfig: any;
    activeBlocks: {
        anchor?: {
            enabled: boolean;
            fixedPosition: 'top' | 'bottom';
            include_in?: string[];
            exclude_from?: string[];
        };
        fixed?: {
            enabled: boolean;
            fixedPosition: 'top' | 'bottom';
            include_in?: string[];
            exclude_from?: string[];
        };
        Topo?: {
            enabled: boolean;
            reference: string;
            insideContainer: number;
            positionReference: 'before' | 'after';
            include_in?: string[];
            exclude_from?: string[];
        };
        Content1?: {
            enabled: boolean;
            reference: string;
            insideContainer: number;
            positionReference: 'before' | 'after';
            include_in?: string[];
            exclude_from?: string[];
        };
        Content2?: {
            enabled: boolean;
            reference: string;
            insideContainer: number;
            positionReference: 'before' | 'after';
            include_in?: string[];
            exclude_from?: string[];
        };
    };
    onSelectElement: (element: {
        tag: string;
        index: number;
        positionType: 'before' | 'after';
    }) => void;
    isSelectionMode: boolean;
    onToggleSelectionMode: (enabled: boolean) => void;
}

export function SitePreview({
                                domain,
                                blockConfig,
                                activeBlocks,
                                onSelectElement,
                                isSelectionMode,
                                onToggleSelectionMode
                            }: SitePreviewProps) {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hoveredElement, setHoveredElement] = useState<any>(null);
    const [iframeReady, setIframeReady] = useState(false);
    const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Normalizar URL adicionando https:// se necessário
    const normalizeUrl = (urlString: string) => {
        if (!urlString) return '';

        // Se já tem protocolo, retorna como está
        if (urlString.startsWith('http://') || urlString.startsWith('https://')) {
            return urlString;
        }

        // Adiciona https://
        return 'https://' + urlString;
    };

    // Carregar domain inicial
    useEffect(() => {
        if (domain) {
            const normalizedUrl = normalizeUrl(domain);
            setUrl(normalizedUrl);
            loadUrlByString(normalizedUrl);
        }
    }, [domain]);

    // Script que será injetado no iframe
    const getInspectorScript = (selectionEnabled: boolean) => `
        (function() {
            let hoveredEl = null;
            let selectedEl = null;
            const selectionMode = ${selectionEnabled};
            
            // Estilo do outline de hover + blocos fixos
            const hoverStyle = document.createElement('style');
            hoverStyle.innerHTML = \`
                .inspector-hover {
                    outline: 2px solid #3b82f6 !important;
                    outline-offset: 2px !important;
                    cursor: ${selectionEnabled ? 'pointer' : 'default'} !important;
                }
                .inspector-selected {
                    outline: 3px solid #10b981 !important;
                    outline-offset: 2px !important;
                    background: rgba(16, 185, 129, 0.05) !important;
                }
                .inspector-info {
                    position: fixed;
                    top: 10px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #1f2937;
                    color: white;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    font-family: monospace;
                    z-index: 9999999;
                    white-space: nowrap;
                    pointer-events: none;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
                }
                
                .selection-mode-banner {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    padding: 8px 16px;
                    text-align: center;
                    font-size: 13px;
                    font-weight: bold;
                    z-index: 9999998;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    font-family: Arial, sans-serif;
                }
                
                /* Blocos Fixos */
                .becker-fixed-block {
                    position: fixed;
                    left: 0;
                    right: 0;
                    background: repeating-linear-gradient(
                        45deg,
                        #dbeafe,
                        #dbeafe 10px,
                        #bfdbfe 10px,
                        #bfdbfe 20px
                    );
                    border: 3px solid #3b82f6;
                    padding: 15px;
                    text-align: center;
                    color: #1e40af;
                    font-weight: bold;
                    font-family: Arial, sans-serif;
                    z-index: 999998;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                
                .becker-fixed-block.top {
                    top: 0;
                }
                
                .becker-fixed-block.bottom {
                    bottom: 0;
                }
                
                .becker-anchor-block {
                    position: fixed;
                    left: 0;
                    right: 0;
                    background: repeating-linear-gradient(
                        45deg,
                        #fef3c7,
                        #fef3c7 10px,
                        #fde68a 10px,
                        #fde68a 20px
                    );
                    border: 3px solid #f59e0b;
                    padding: 15px;
                    text-align: center;
                    color: #92400e;
                    font-weight: bold;
                    font-family: Arial, sans-serif;
                    z-index: 999999;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                
                .becker-anchor-block.top {
                    top: 0;
                }
                
                .becker-anchor-block.bottom {
                    bottom: 0;
                }
                
                /* Blocos de Display (Topo, Content1, Content2) */
                .becker-display-block {
                    background: repeating-linear-gradient(
                        45deg,
                        #fce7f3,
                        #fce7f3 10px,
                        #fbcfe8 10px,
                        #fbcfe8 20px
                    );
                    border: 3px solid #ec4899;
                    padding: 20px;
                    text-align: center;
                    color: #9f1239;
                    font-weight: bold;
                    font-family: Arial, sans-serif;
                    margin: 15px 0;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }
            \`;
            document.head.appendChild(hoverStyle);
            
            // Mostrar banner se modo de seleção estiver ativo
            if (selectionMode) {
                const banner = document.createElement('div');
                banner.className = 'selection-mode-banner';
                banner.innerHTML = '🎯 MODO DE SELEÇÃO ATIVO - Clique em um elemento para definir a posição do bloco';
                document.body.appendChild(banner);
            }
            
            // Função para renderizar blocos de display
            window.renderDisplayBlocks = function(blocks, currentPath) {
                // Remover blocos de display existentes
                document.querySelectorAll('.becker-display-block').forEach(el => el.remove());
                
                if (!currentPath) {
                    currentPath = window.location.pathname;
                }
                
                const currentFullUrl = window.location.href;
                
                window.parent.postMessage({
                    type: 'debug',
                    data: {
                        currentPath: currentPath,
                        currentFullUrl: currentFullUrl,
                        blocks: blocks
                    }
                }, '*');
                
                console.log('[IFRAME] Current Full URL:', currentFullUrl);
                console.log('[IFRAME] Current Path (from parent):', currentPath);
                console.log('[IFRAME] Active Blocks:', blocks);
                
                if (blocks.Topo && blocks.Topo.enabled) {
                    const shouldShow = shouldShowBlock(blocks.Topo, currentPath);
                    console.log('[IFRAME] Topo - shouldShow:', shouldShow);
                    if (shouldShow) {
                        insertDisplayBlock('TOPO', blocks.Topo, currentPath);
                    }
                }
                
                if (blocks.Content1 && blocks.Content1.enabled) {
                    const shouldShow = shouldShowBlock(blocks.Content1, currentPath);
                    console.log('[IFRAME] Content1 - shouldShow:', shouldShow);
                    if (shouldShow) {
                        insertDisplayBlock('CONTENT 1', blocks.Content1, currentPath);
                    }
                }
                
                if (blocks.Content2 && blocks.Content2.enabled) {
                    const shouldShow = shouldShowBlock(blocks.Content2, currentPath);
                    console.log('[IFRAME] Content2 - shouldShow:', shouldShow);
                    if (shouldShow) {
                        insertDisplayBlock('CONTENT 2', blocks.Content2, currentPath);
                    }
                }
            };
            
            function shouldShowBlock(blockConfig, currentPath) {
                console.log('[IFRAME] Checking block:', {
                    currentPath: currentPath,
                    include_in: blockConfig.include_in,
                    exclude_from: blockConfig.exclude_from
                });
                
                if (blockConfig.include_in && blockConfig.include_in.length > 0) {
                    const shouldInclude = blockConfig.include_in.some(function(path) {
                        const matches = currentPath.startsWith(path);
                        console.log('[IFRAME] Checking include:', path, '→', matches);
                        return matches;
                    });
                    console.log('[IFRAME] Include check result:', shouldInclude);
                    if (!shouldInclude) {
                        return false;
                    }
                }
                
                if (blockConfig.exclude_from && blockConfig.exclude_from.length > 0) {
                    const shouldExclude = blockConfig.exclude_from.some(function(path) {
                        const matches = currentPath.startsWith(path);
                        console.log('[IFRAME] Checking exclude:', path, '→', matches);
                        return matches;
                    });
                    console.log('[IFRAME] Exclude check result:', shouldExclude);
                    if (shouldExclude) {
                        return false;
                    }
                }
                
                return true;
            }
            
            function insertDisplayBlock(name, config, currentPath) {
                const elements = document.querySelectorAll(config.reference);
                
                if (elements.length === 0) {
                    console.warn('Nenhum elemento encontrado para:', config.reference);
                    return;
                }
                
                const targetIndex = config.insideContainer - 1;
                const targetElement = elements[targetIndex];
                
                if (!targetElement) {
                    console.warn('Elemento não encontrado no índice:', config.insideContainer);
                    return;
                }
                
                const adBlock = document.createElement('div');
                adBlock.className = 'becker-display-block';
                adBlock.innerHTML = '📢 ' + name + ' BLOCK<br>' +
                    '<small>728x90 - Ref: &lt;' + config.reference + '&gt; #' + config.insideContainer + ' (' + config.positionReference + ')</small><br>' +
                    '<small style="color: #be185d;">Path: ' + currentPath + '</small>';
                
                if (config.positionReference === 'before') {
                    targetElement.parentNode.insertBefore(adBlock, targetElement);
                } else {
                    targetElement.parentNode.insertBefore(adBlock, targetElement.nextSibling);
                }
            }
            
            window.renderFixedBlocks = function(blocks, currentPath) {
                document.querySelectorAll('.becker-fixed-block, .becker-anchor-block').forEach(el => el.remove());
                
                if (!currentPath) {
                    currentPath = window.location.pathname;
                }
                
                if (blocks.anchor && blocks.anchor.enabled && shouldShowBlock(blocks.anchor, currentPath)) {
                    const anchorBlock = document.createElement('div');
                    anchorBlock.className = 'becker-anchor-block ' + blocks.anchor.fixedPosition;
                    anchorBlock.innerHTML = '⚓ ANCHOR BLOCK (' + blocks.anchor.fixedPosition.toUpperCase() + ')<br><small>728x90 - Bloco Âncora</small>';
                    document.body.appendChild(anchorBlock);
                }
                
                if (blocks.fixed && blocks.fixed.enabled && shouldShowBlock(blocks.fixed, currentPath)) {
                    const fixedBlock = document.createElement('div');
                    fixedBlock.className = 'becker-fixed-block ' + blocks.fixed.fixedPosition;
                    fixedBlock.innerHTML = '📌 FIXED BLOCK (' + blocks.fixed.fixedPosition.toUpperCase() + ')<br><small>728x90 - Bloco Fixo</small>';
                    document.body.appendChild(fixedBlock);
                }
            };
            
            // Interceptar cliques
            document.addEventListener('click', function(e) {
                const link = e.target.closest('a');
                
                // Se clicar em link E não estiver em modo de seleção, navegar
                if (link && link.href && !selectionMode) {
                    e.preventDefault();
                    window.parent.postMessage({
                        type: 'navigate',
                        url: link.href
                    }, '*');
                    return;
                }
                
                // Se modo de seleção estiver DESATIVADO, não fazer nada
                if (!selectionMode) {
                    return;
                }
                
                // Modo de seleção ATIVO - copiar info e enviar para parent
                e.preventDefault();
                e.stopPropagation();
                
                const info = getElementInfo(e.target);
                const textToCopy = \`<\${info.tag}> #\${info.index}/\${info.total}\`;
                
                // Copiar para clipboard
                navigator.clipboard.writeText(textToCopy).then(function() {
                    const feedback = document.createElement('div');
                    feedback.style.cssText = \`
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        background: #10b981;
                        color: white;
                        padding: 12px 24px;
                        border-radius: 8px;
                        font-size: 14px;
                        font-weight: bold;
                        z-index: 99999999;
                        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
                    \`;
                    feedback.textContent = '✓ Posição definida: ' + textToCopy;
                    document.body.appendChild(feedback);
                    
                    setTimeout(function() {
                        feedback.remove();
                    }, 2000);
                }).catch(function(err) {
                    console.error('Erro ao copiar:', err);
                });
                
                // Enviar info para o parent
                window.parent.postMessage({
                    type: 'select',
                    data: info
                }, '*');
            });
            
            function getElementInfo(el) {
                const tag = el.tagName.toLowerCase();
                const elements = document.querySelectorAll(tag);
                const index = Array.from(elements).indexOf(el) + 1;
                const totalCount = elements.length;
                
                return {
                    tag: tag,
                    index: index,
                    total: totalCount,
                    classes: el.className,
                    id: el.id,
                    text: el.textContent?.substring(0, 50) + '...'
                };
            }
            
            function createInfoTooltip(info) {
                const tooltip = document.createElement('div');
                tooltip.className = 'inspector-info';
                tooltip.textContent = \`<\${info.tag}> #\${info.index}/\${info.total}\`;
                return tooltip;
            }
            
            // Hover handler - só mostrar se modo de seleção estiver ativo
            document.addEventListener('mouseover', function(e) {
                if (!selectionMode) return;
                
                e.stopPropagation();
                
                if (hoveredEl && hoveredEl !== selectedEl) {
                    hoveredEl.classList.remove('inspector-hover');
                    const oldTooltip = document.body.querySelector('.inspector-info');
                    if (oldTooltip) oldTooltip.remove();
                }
                
                hoveredEl = e.target;
                
                if (hoveredEl !== selectedEl) {
                    hoveredEl.classList.add('inspector-hover');
                    const info = getElementInfo(hoveredEl);
                    const tooltip = createInfoTooltip(info);
                    document.body.appendChild(tooltip);
                }
                
                window.parent.postMessage({
                    type: 'hover',
                    data: getElementInfo(hoveredEl)
                }, '*');
            });
            
            document.addEventListener('mouseout', function(e) {
                if (!selectionMode) return;
                
                if (hoveredEl && hoveredEl !== selectedEl) {
                    hoveredEl.classList.remove('inspector-hover');
                    const tooltip = document.body.querySelector('.inspector-info');
                    if (tooltip) tooltip.remove();
                }
            });
            
            window.addEventListener('message', function(e) {
                if (e.data.type === 'updatePosition') {
                    window.currentPositionReference = e.data.position;
                }
                if (e.data.type === 'renderFixedBlocks') {
                    window.renderFixedBlocks(e.data.blocks, e.data.currentPath);
                }
                if (e.data.type === 'renderDisplayBlocks') {
                    window.renderDisplayBlocks(e.data.blocks, e.data.currentPath);
                }
            });
            
            window.currentPositionReference = 'after';
        })();
    `;

    // Carregar URL no iframe via API
    const loadUrl = async () => {
        if (!url) return;
        const normalizedUrl = normalizeUrl(url);
        setUrl(normalizedUrl);
        await loadUrlByString(normalizedUrl);
    };

    const loadUrlByString = async (urlString: string) => {
        setIsLoading(true);
        setIframeReady(false);

        try {
            const data = await getPreviewHtml(urlString);

            const iframe = iframeRef.current;
            if (!iframe) return;

            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (doc) {
                const urlObj = new URL(urlString);
                const currentPath = urlObj.pathname;

                console.log('📌 Loading URL:', {
                    full: urlString,
                    pathname: currentPath,
                    origin: urlObj.origin
                });

                (iframe as any).__currentPath = currentPath;
                (iframe as any).__currentUrl = urlString;

                doc.open();
                doc.write(data.html);
                doc.close();
            }
        } catch (error) {
            console.error('Erro ao carregar URL:', error);
            setIsLoading(false);
        }
    };

    // Injetar script inspector quando iframe carregar OU quando isSelectionMode mudar
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !iframeReady) return;

        try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) return;

            // Remover scripts antigos
            const oldScripts = iframeDoc.querySelectorAll('script[data-inspector="true"]');
            oldScripts.forEach(s => s.remove());

            // Remover banners e estilos antigos
            const oldBanners = iframeDoc.querySelectorAll('.selection-mode-banner');
            oldBanners.forEach(b => b.remove());

            // Injetar novo script com estado atual
            const script = iframeDoc.createElement('script');
            script.setAttribute('data-inspector', 'true');
            script.textContent = getInspectorScript(isSelectionMode);
            iframeDoc.body.appendChild(script);

            // Renderizar blocos
            setTimeout(() => {
                if (iframe?.contentWindow) {
                    const currentPath = (iframe as any).__currentPath || '/';

                    iframe.contentWindow.postMessage({
                        type: 'renderFixedBlocks',
                        blocks: activeBlocks,
                        currentPath: currentPath
                    }, '*');
                    iframe.contentWindow.postMessage({
                        type: 'renderDisplayBlocks',
                        blocks: activeBlocks,
                        currentPath: currentPath
                    }, '*');
                }
            }, 100);
        } catch (error) {
            console.error('Erro ao injetar script:', error);
        }
    }, [isSelectionMode, iframeReady, activeBlocks]);

    // Handler do load do iframe
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const handleLoad = () => {
            setIsLoading(false);
            setIframeReady(true);
        };

        iframe.addEventListener('load', handleLoad);
        return () => iframe.removeEventListener('load', handleLoad);
    }, []);

    // Listener para mensagens do iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'hover') {
                setHoveredElement(event.data.data);
            } else if (event.data.type === 'select') {
                onSelectElement({
                    tag: event.data.data.tag,
                    index: event.data.data.index,
                    positionType: blockConfig?.positionReference || 'after'
                });
                // Desativar modo de seleção após selecionar
                onToggleSelectionMode(false);
            } else if (event.data.type === 'navigate') {
                setUrl(event.data.url);
                loadUrlByString(event.data.url);
            } else if (event.data.type === 'debug') {
                console.log('🔍 [DEBUG FROM IFRAME]', event.data.data);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [blockConfig, onSelectElement, onToggleSelectionMode]);

    // Atualizar posição de referência
    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow && blockConfig?.positionReference) {
            iframe.contentWindow.postMessage({
                type: 'updatePosition',
                position: blockConfig.positionReference
            }, '*');
        }
    }, [blockConfig?.positionReference]);

    // Renderizar blocos fixos quando activeBlocks mudar
    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow && iframeReady) {
            const currentPath = (iframe as any).__currentPath || '/';

            iframe.contentWindow.postMessage({
                type: 'renderFixedBlocks',
                blocks: activeBlocks,
                currentPath: currentPath
            }, '*');
        }
    }, [activeBlocks, iframeReady]);

    // Renderizar blocos de display quando activeBlocks mudar
    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow && iframeReady) {
            const currentPath = (iframe as any).__currentPath || '/';

            iframe.contentWindow.postMessage({
                type: 'renderDisplayBlocks',
                blocks: activeBlocks,
                currentPath: currentPath
            }, '*');
        }
    }, [activeBlocks, iframeReady]);

    // Calcular estilo do iframe baseado no device mode
    const getIframeContainerStyle = () => {
        const config = DEVICE_CONFIGS[deviceMode];

        if (deviceMode === 'desktop') {
            return {
                width: '100%',
                height: '100%',
                margin: '0',
                borderRadius: '0',
                boxShadow: 'none',
                background: 'transparent'
            };
        }

        return {
            width: `${config.width}px`,
            height: `${config.height}px`,
            margin: '0 auto',
            borderRadius: deviceMode === 'mobile' ? '40px' : '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)',
            background: '#1f2937'
        };
    };

    const getIframeStyle = () => {
        if (deviceMode === 'desktop') {
            return {
                width: '100%',
                height: '100%',
                borderRadius: '0'
            };
        }

        return {
            width: '100%',
            height: '100%',
            borderRadius: deviceMode === 'mobile' ? '32px' : '12px'
        };
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Toolbar */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex gap-2 items-center mb-3">
                    <Input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://exemplo.com.br/pagina"
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && loadUrl()}
                    />
                    <Button onClick={loadUrl} disabled={!url || isLoading} size="sm">
                        {isLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {/* Device Mode Toggle */}
                <div className="flex gap-2 items-center mb-3">
                    <div className="flex bg-gray-100 rounded-lg p-1 flex-1">
                        <Button
                            onClick={() => setDeviceMode('desktop')}
                            variant="ghost"
                            size="sm"
                            className={`flex-1 gap-2 ${deviceMode === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                        >
                            <Monitor className="h-4 w-4" />
                            <span className="hidden sm:inline">Desktop</span>
                        </Button>
                        <Button
                            onClick={() => setDeviceMode('tablet')}
                            variant="ghost"
                            size="sm"
                            className={`flex-1 gap-2 ${deviceMode === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                        >
                            <Tablet className="h-4 w-4" />
                            <span className="hidden sm:inline">Tablet</span>
                        </Button>
                        <Button
                            onClick={() => setDeviceMode('mobile')}
                            variant="ghost"
                            size="sm"
                            className={`flex-1 gap-2 ${deviceMode === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                        >
                            <Smartphone className="h-4 w-4" />
                            <span className="hidden sm:inline">Mobile</span>
                        </Button>
                    </div>
                </div>

                {/* Device Info Badge */}
                {deviceMode !== 'desktop' && (
                    <div className="text-xs text-gray-500 text-center mb-3">
                        {DEVICE_CONFIGS[deviceMode].label}: {DEVICE_CONFIGS[deviceMode].width} × {DEVICE_CONFIGS[deviceMode].height}px
                    </div>
                )}

                {/* Botão de Modo de Seleção */}
                <Button
                    onClick={() => onToggleSelectionMode(!isSelectionMode)}
                    variant={isSelectionMode ? 'default' : 'outline'}
                    size="sm"
                    className={`w-full ${isSelectionMode ? 'bg-green-600 hover:bg-green-700' : ''}`}
                >
                    {isSelectionMode ? (
                        <>
                            <X className="h-4 w-4 mr-2" />
                            Cancelar Seleção
                        </>
                    ) : (
                        <>
                            <MousePointerClick className="h-4 w-4 mr-2" />
                            Ativar Modo de Seleção
                        </>
                    )}
                </Button>
            </div>

            {/* Info Panel - Hovered Element */}
            {hoveredElement && isSelectionMode && (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs">
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-blue-900">
                            &lt;{hoveredElement.tag}&gt; #{hoveredElement.index}/{hoveredElement.total}
                        </span>
                        {hoveredElement.id && (
                            <span className="text-blue-600">id="{hoveredElement.id}"</span>
                        )}
                    </div>
                </div>
            )}

            {/* Iframe Container */}
            <div
                ref={containerRef}
                className={`flex-1 relative overflow-auto ${deviceMode !== 'desktop' ? 'bg-gray-200 p-6' : 'bg-gray-100'}`}
            >
                <div
                    className="transition-all duration-300 ease-in-out overflow-hidden"
                    style={getIframeContainerStyle()}
                >
                    {/* Device Frame Notch (Mobile Only) */}
                    {deviceMode === 'mobile' && (
                        <div className="h-8 bg-gray-800 flex items-center justify-center">
                            <div className="w-20 h-5 bg-black rounded-full" />
                        </div>
                    )}

                    <iframe
                        ref={iframeRef}
                        className="border-0 bg-white"
                        style={getIframeStyle()}
                        sandbox="allow-same-origin allow-scripts"
                        title="Site Preview"
                    />

                    {/* Device Frame Bottom Bar (Mobile Only) */}
                    {deviceMode === 'mobile' && (
                        <div className="h-6 bg-gray-800 flex items-center justify-center">
                            <div className="w-28 h-1 bg-gray-600 rounded-full" />
                        </div>
                    )}
                </div>

                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
                    </div>
                )}

                {!iframeReady && !isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                        <div className="text-center text-gray-500">
                            <Code className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm font-medium">Digite uma URL para começar</p>
                            <p className="text-xs mt-1">O preview do site será carregado aqui</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}